// src/components/select/SelectTableDialog.tsx
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Stack, Typography, Box } from '@mui/material';
import { CustomDataGrid } from './CustomDataGrid';
import { GridColDef } from '@mui/x-data-grid-premium';
import { tableSettingsProps } from 'src/core/tables/useTableSettings';
import { TableSelector } from 'src/core/tables/useTableSelector';
import { CustomField } from 'src/core/forms/CustomField';
import CustomButton from 'src/core/ui/CustomButton';

const SelectTableDialog = <Row,>({ open, onClose, openSelector, columns, tableSettings, message, messageTitle = 'Selection notice', clearMessage, applySelection, clearSelection, selectedCount, applyButtonText = 'Add selected' }: SelectTableDialogProps<Row>) => {
  const typedTableSettings = tableSettings as tableSettingsProps;
  const showActions = Boolean(typedTableSettings?.checkboxSelection && applySelection);

  return (
    <>
      <Dialog fullWidth maxWidth="xl" open={open} onClose={onClose}>
        <DialogTitle sx={{ pb: 1.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={2}>
            <Typography variant="h6">Select items</Typography>
            {/* Quick filter next to the title */}
            <Box sx={{ minWidth: 0, width: { xs: '100%', sm: 240 }, maxWidth: 280 }}>
              <CustomField stateProps={{ state: typedTableSettings.quickFilterValues, setState: typedTableSettings.setQuickFilterValues, }} options={{ name: 'QuickFilter', label: 'Quick search', height: '40px', topMargin: '0px', className: 'quick-search', }} />
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <CustomDataGrid columns={columns as GridColDef[]} tableSettings={typedTableSettings} />
        </DialogContent>
        {showActions && (
          <DialogActions sx={{ px: 2, py: 1.5, flexWrap: 'wrap', gap: 1, '& > :not(style) ~ :not(style)': { ml: 0 } }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mr: 'auto', pl: 0.5, flexBasis: { xs: '100%', sm: 'auto' } }}>
              <Typography variant="body2">
                {selectedCount > 0 ? `${selectedCount} selected` : 'No selection'}
              </Typography>
            </Stack>
            <Box sx={{ width: 'fit-content' }}><CustomButton title="Clear" onClick={clearSelection} disabled={!selectedCount} variant="text" /></Box>
            <Box sx={{ width: 'fit-content', maxWidth: '100%' }}><CustomButton title={`${applyButtonText}${selectedCount ? ` (${selectedCount})` : ''}`} onClick={() => applySelection?.({ keepOpen: false })} disabled={!selectedCount} /></Box>
          </DialogActions>
        )}
      </Dialog>
      {message && clearMessage && (
        <Dialog open onClose={clearMessage}>
          <DialogTitle>{messageTitle}</DialogTitle>
          <DialogContent>
            <DialogContentText>{message}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Box sx={{ width: 'fit-content' }}><CustomButton title="OK" variant="text" onClick={clearMessage} /></Box>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default SelectTableDialog;

export type SelectTableDialogProps<Row = any> = TableSelector<Row> & {
  applyButtonText?: string;
  messageTitle?: string;
};
